import Image from 'next/image';
import dynamic from "next/dynamic";
import { getGalleryCategoryUrl } from "@/lib/gallery";
import { getReadableImageUrl } from "@/lib/directus";

const Link = dynamic(() => import("@/components/ui/Link"));

interface GalleryCategoryData {
  images: {
    images_id: {
      alt: string;
      image_file: {
        image: {
          id: string;
          filename_download: string;
        };
      };
    };
  }[];
  categories: {
    title: string;
    slug: string;
    category: string;
  }[];
}

interface GalleryIndexProps {
  data: GalleryCategoryData;
}

export default function GalleryIndex({ data }: GalleryIndexProps) {
  // Group categories by category type
  const groupedCategories = data.categories.reduce((acc, category) => {
    const categoryType = category.category || 'other';
    if (!acc[categoryType]) {
      acc[categoryType] = [];
    }
    acc[categoryType].push(category);
    return acc;
  }, {} as Record<string, typeof data.categories>);
  
  const categoryGroups = Object.entries(groupedCategories);
  
  return (
    <section className="w-full bg-white py-8 xl:pb-32">
      {/* Breadcrumb */}
      <nav className="container mx-auto max-w-screen-2xl pb-8 px-8 lg:px-0">
        <a href="/" className="text-primary text-base font-normal font-greycliff">
          Home
        </a>  
        <span className="text-primary text-base font-normal font-greycliff"> / Gallery</span>

      </nav>

      {/* Main Content */}
      <div className="container mx-auto max-w-screen-2xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {categoryGroups.map(([categoryType, categories], index) => {
            // Get image for this category if available
            const imageData = data.images[index]?.images_id;
            const imageUrl = imageData?.image_file?.image?.id && imageData?.image_file?.image?.filename_download ? 
              getReadableImageUrl(imageData.image_file.image.id, imageData.image_file.image.filename_download) : 
              '/images/rhinoplasty-gallery.png';
            const imageAlt = imageData?.alt || categoryType;
            
            return (
              <div key={categoryType} className="block space-y-5">
                <div className="overflow-hidden lg:rounded-[1.25rem] mb-8">
                  <Image
                    src={imageUrl}
                    alt={imageAlt}
                    width={632}
                    height={370}
                    className="w-full h-auto object-cover"
                    quality={90}
                  />
                </div>
                <div className="block space-y-5 px-8 lg:px-0">
                  <h2 className="text-[2rem] lg:text-[3.375rem] font-light font-essonnes text-primary mb-8">
                    {categoryType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </h2>
                  <ul className="block space-y-3">
                    {categories.map((category) => (
                      <li key={category.slug} className="text-primary text-lg lg:text-xl lg:font-medium font-greycliff leading-[1.87rem] tracking-tight">
                        <Link href={getGalleryCategoryUrl(category.slug) || "#"} className="hover:opacity-80">
                          {category.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
